import * as topojson from 'topojson';
import usa from './topo.usa.min.json';
import citiesRaw from './cities.min.json';

import _ from 'lodash';

class TopoUtils {

  constructor() {
    this.cache = {};
  }

  /**
   * Returns a topojson model for the usa including
   *
   * land : topojson feature of the us outline
   * borders : topojson mesh of state borders
   * states : topojson feature collection of all us states
   * counties : topojson feature collection of all us counties
   *
   * @returns {{land: *, borders: *, states: (features)}}
   */
  getNationalTopography() {
    const key = `national`;

    if (!this.cache[key]) {
      const land = topojson.feature(usa, usa.objects.land);
      const states = topojson.feature(usa, usa.objects.states).features;
      const counties = topojson.feature(usa, usa.objects.counties).features;
      const borders = topojson.mesh(usa, usa.objects.states, function (a, b) {
        return a !== b;
      });

      this.cache[key] = {land, borders, states, counties};
    }

    return this.cache[key] || {};
  }

  /**
   * Returns a topojson model for a state relating to the supplied fip
   * uses an internal cache to speed up subsequent requests for the state topo
   *
   * land : topojson feature of the state outline
   * borders : topojson mesh of states internal county borders
   * states : topojson feature collection of all counties in state
   *
   * @param fips - integer fips code for state desired
   * @returns {{land: *, borders: *, counties: (features)}}
   */
  getStateTopography(fips) {
    const key = `s-${fips}`;

    if (fips && !this.cache[key]) {
      const countyTopo = this.getCountiesGeometry(fips);
      const stateTopo = this.getStateGeometry(fips);
      const cities = _.filter(citiesRaw, ['sf', fips]);
      const land = topojson.feature(usa, stateTopo);
      const counties = topojson.feature(usa, countyTopo).features;
      const borders = topojson.mesh(usa, countyTopo, function (a, b) {
        return a !== b;
      });

      this.cache[key] = {land, borders, counties, cities};
    }

    return this.cache[key] || {};
  }

  getCountyTopography(fips) {
    const key = `c-${fips}`;

    if (fips && !this.cache[key]) {
      const countyTopo = _.find(usa.objects.counties.geometries, ['id', fips]);
      const land = topojson.feature(usa, countyTopo);
      const bounds = this.getGeometryBounds(land);
      const center = [
        bounds[0] + bounds[1] / 2,
        bounds[2] + bounds[3] / 2,
      ];

      this.cache[key] = {land, bounds, center};
    }

    return this.cache[key] || {};
  }

  /**
   * Returns a filtered GeometryCollection containing only the counties
   * contained withing the state specified by stateFips.
   * If no stateFips provided, all counties are returned.
   *
   * @param stateFips - integer fips code for state desired
   * @returns {{type: *, geometries}}
   */
  getCountiesGeometry(stateFips) {
    return {
      type: "GeometryCollection",
      geometries: _.filter(usa.objects.counties.geometries, county => {
        return !stateFips || stateFips === Math.floor(county.id / 1000);
      })
    };
  }

  /**
   * Returns a filtered GeometryCollection containing only
   * the state shape specified by stateFips.
   * If no stateFips provided, all us state geometries are returned.
   *
   * @param stateFips - integer fips code for state desired
   * @returns {{type: *, geometries}}
   */
  getStateGeometry(stateFips) {
    return {
      type: "GeometryCollection",
      geometries: _.filter(usa.objects.states.geometries, state => {
        return !stateFips || stateFips === state.id;
      })
    };
  }

  getGeometryBounds(gj) {
    if (!gj.hasOwnProperty('type')) return;

    const coords = this.getCoordinatesDump(gj);
    const bbox = [
      Number.POSITIVE_INFINITY,
      Number.POSITIVE_INFINITY,
      Number.NEGATIVE_INFINITY,
      Number.NEGATIVE_INFINITY
    ];

    return coords.reduce(function (prev, coord) {
      return [
        Math.min(coord[0], prev[0]),
        Math.min(coord[1], prev[1]),
        Math.max(coord[0], prev[2]),
        Math.max(coord[1], prev[3])
      ];
    }, bbox);
  };

  getCoordinatesDump(gj) {
    var coords;
    if (gj.type === 'Point') {
      coords = [gj.coordinates];
    } else if (gj.type === 'LineString' || gj.type === 'MultiPoint') {
      coords = gj.coordinates;
    } else if (gj.type === 'Polygon' || gj.type === 'MultiLineString') {
      coords = gj.coordinates.reduce(function (dump, part) {
        return dump.concat(part);
      }, []);
    } else if (gj.type === 'MultiPolygon') {
      coords = gj.coordinates.reduce(function (dump, poly) {
        return dump.concat(poly.reduce(function (points, part) {
          return points.concat(part);
        }, []));
      }, []);
    } else if (gj.type === 'Feature') {
      coords = this.getCoordinatesDump(gj.geometry);
    } else if (gj.type === 'GeometryCollection') {
      coords = gj.geometries.reduce(function (dump, g) {
        return dump.concat(this.getCoordinatesDump(g));
      }, []);
    } else if (gj.type === 'FeatureCollection') {
      coords = gj.features.reduce(function (dump, f) {
        return dump.concat(this.getCoordinatesDump(f));
      }, []);
    }
    return coords;
  }

}

export let topoUtils = new TopoUtils();


// WEBPACK FOOTER //
// ./src/charts/utils/topo-utils.js